import parse from 'html-react-parser'
import React from 'react'

interface HtmlRendererProps {
  htmlContent: string
  maxLength?: number
  className?: string
  /**
   * Render links/buttons for preview only: they stay visible and styled but cannot be
   * clicked, focused or navigated (used for email body previews in mail logs).
   */
  disableNavigation?: boolean
}

/**
 * Comprehensive HTML sanitization function to prevent XSS attacks
 * This function removes dangerous HTML tags, attributes, and content
 * including SVG-based XSS attacks
 */
const sanitizeHtmlContent = (html: string | null | undefined): string => {
  if (!html || typeof html !== 'string') {
    return ''
  }

  let sanitized = html

  // Remove script tags and their content (case-insensitive, multiline)
  sanitized = sanitized.replace(/<script[\s\S]*?<\/script>/gi, '')

  // Remove event handlers (onclick, onerror, onload, etc.)
  sanitized = sanitized.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '')
  sanitized = sanitized.replace(/\s*on\w+\s*=\s*[^\s>]*/gi, '')

  // Remove javascript: protocol in href, src, and other attributes
  sanitized = sanitized.replace(/javascript:/gi, '')

  // Remove dangerous SVG content that may contain embedded scripts
  // This handles the specific attack vector mentioned in the penetration test
  sanitized = sanitized.replace(/<svg[\s\S]*?<\/svg>/gi, (match) => {
    // Check if SVG contains script tags or event handlers
    if (
      /<script/i.test(match) ||
      /on\w+\s*=/i.test(match) ||
      /javascript:/i.test(match) ||
      /<iframe/i.test(match) ||
      /<embed/i.test(match) ||
      /<object/i.test(match)
    ) {
      return '' // Remove dangerous SVG
    }
    // Allow safe SVG but remove any event handlers
    return match.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '').replace(/\s*on\w+\s*=\s*[^\s>]*/gi, '')
  })

  // Remove iframe, embed, object tags (potential XSS vectors)
  sanitized = sanitized.replace(/<iframe[\s\S]*?<\/iframe>/gi, '')
  sanitized = sanitized.replace(/<embed[\s\S]*?>/gi, '')
  sanitized = sanitized.replace(/<object[\s\S]*?<\/object>/gi, '')

  // Remove style tags that might contain malicious CSS
  sanitized = sanitized.replace(/<style[\s\S]*?<\/style>/gi, '')

  // Remove link tags with javascript: or data: protocols in href
  sanitized = sanitized.replace(/<link[^>]*href\s*=\s*["']?(javascript|data):[^>]*>/gi, '')

  // Remove meta tags with javascript: in content
  sanitized = sanitized.replace(/<meta[^>]*content\s*=\s*["']?javascript:[^>]*>/gi, '')

  // Normalize whitespace
  sanitized = sanitized.replace(/\s+/g, ' ').replace(/>\s+</g, '><').trim()

  return sanitized
}

/** Attributes that can trigger a navigation or a submit */
const NAVIGATION_ATTRIBUTES =
  /\s(?:href|target|action|formaction|ping|download|xlink:href)\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi

/**
 * Neutralises every navigable/clickable element while keeping it in the markup so the
 * preview still looks exactly like the original email (buttons, CTA links, etc.).
 * Anchors lose their href/target, form controls get disabled, and everything is removed
 * from the tab order so it cannot be activated with the keyboard either.
 */
const disableHtmlNavigation = (html: string): string => {
  const cleanAttrs = (attrs: string) => attrs.replace(NAVIGATION_ATTRIBUTES, '').replace(/\/\s*$/, '')

  return html
    .replace(
      /<(a|area|form|map)\b([^>]*)>/gi,
      (_match, tag: string, attrs: string) => `<${tag}${cleanAttrs(attrs)} tabindex="-1" data-nav-disabled="true">`
    )
    .replace(
      /<(button|input|select|textarea)\b([^>]*)>/gi,
      (_match, tag: string, attrs: string) =>
        `<${tag}${cleanAttrs(attrs)} disabled tabindex="-1" data-nav-disabled="true">`
    )
}

/** Blocks any click/activation coming from inside a preview-only HTML block */
const blockNavigation = (event: React.SyntheticEvent) => {
  event.preventDefault()
  event.stopPropagation()
}

const NON_INTERACTIVE_CLASS =
  '[&_a]:pointer-events-none [&_a]:cursor-default [&_button]:pointer-events-none [&_button]:cursor-default [&_input]:pointer-events-none [&_area]:pointer-events-none'

const HtmlRenderer: React.FC<HtmlRendererProps> = ({ htmlContent, className, disableNavigation }) => {
  // Sanitize content before parsing to prevent XSS
  const sanitizedContent = disableNavigation
    ? disableHtmlNavigation(sanitizeHtmlContent(htmlContent))
    : sanitizeHtmlContent(htmlContent)

  let parsedContent: React.ReactNode = null
  try {
    parsedContent = parse(sanitizedContent)
  } catch {
    parsedContent = (
      <div
        className='text-sm font-medium whitespace-pre-wrap break-words overflow-x-auto text-wrap'
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
      />
    )
  }

  return (
    <div
      className={disableNavigation ? `${className ?? ''} ${NON_INTERACTIVE_CLASS}` : className}
      {...(disableNavigation
        ? { onClickCapture: blockNavigation, onAuxClickCapture: blockNavigation, onSubmitCapture: blockNavigation }
        : {})}
    >
      <div className='text-editor'>{parsedContent}</div>
    </div>
  )
}

export default HtmlRenderer

export const SanitizedHtmlRenderer: React.FC<HtmlRendererProps> = ({ htmlContent, className, disableNavigation }) => {
  const sanitizedContent = disableNavigation
    ? disableHtmlNavigation(sanitizeHtmlContent(htmlContent))
    : sanitizeHtmlContent(htmlContent)

  return (
    <div
      className={`${className}  text-editor ${disableNavigation ? NON_INTERACTIVE_CLASS : ''}`}
      {...(disableNavigation
        ? { onClickCapture: blockNavigation, onAuxClickCapture: blockNavigation, onSubmitCapture: blockNavigation }
        : {})}
    >
      <div
        className='text-sm font-medium whitespace-pre-wrap break-words overflow-x-auto text-wrap'
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
      />
    </div>
  )
}
